import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { businessEmail, RadioGroupItem, useTypedAppFormContext } from '@/components/Form';
import { Field, FieldLabel } from '@/components/Form/fields';
import InfoPopover from '@/components/InfoPopover';
import { Text } from '@/components/typography';
import { MultiOptionText } from '@/components/typography/MultiOptionText';
import { maxEmailLength, maxUserNameLength } from '@/constants/numbers';
import { useMe } from '@/providers/User';

import { type UserDetailFormType } from './UserFormType';

type Props = {
  isEditing: boolean;
  isSaving: boolean;
  isEmailEditable?: boolean;
  isDigestDeliveryEditable?: boolean;
  userId: string;
};

const UserDetailInfoFields = ({
  isEditing,
  isSaving,
  isEmailEditable,
  isDigestDeliveryEditable = false,
  userId,
}: Props) => {
  const form = useTypedAppFormContext({ defaultValues: {} as UserDetailFormType });
  const user = useMe();
  const isMe = user?.id === userId;
  const isAdmin = user?.isAdmin || user?.isC99Superuser;
  const digestDeliveryFrequencyOptions: { value: number; label: string }[] = [
    { value: 0, label: t`Never` },
    { value: 2, label: t`Every 2 weeks` },
    { value: 4, label: t`Every 4 weeks` },
  ];

  return (
    <div className="flex w-full max-w-[400px] flex-col gap-6">
      <form.AppField
        name="name"
        validators={{
          onSubmit: z
            .string()
            .trim()
            .min(1, t`Name is required`),
        }}
      >
        {(field) => (
          <field.Field>
            <FieldLabel htmlFor="user-detail-name">
              <Trans>Name</Trans>
            </FieldLabel>
            {isEditing ? (
              <field.TextInput
                id="user-detail-name"
                autoComplete="name"
                spellCheck={false}
                autoCapitalize="words"
                autoFocus
                maxLength={maxUserNameLength}
                disabled={isSaving}
              />
            ) : (
              <Text variant="body1">{field.state.value}</Text>
            )}
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
      <form.AppField
        name="email"
        validators={{ onSubmit: isEmailEditable ? businessEmail() : undefined }}
      >
        {(field) => (
          <field.Field>
            <FieldLabel htmlFor="user-detail-email">
              <Trans>Email</Trans>
            </FieldLabel>
            {isEditing && isEmailEditable ? (
              <field.TextInput
                id="user-detail-email"
                inputMode="email"
                placeholder={t`name@work-email.com`}
                autoComplete="email"
                spellCheck={false}
                autoCapitalize="none"
                maxLength={maxEmailLength}
                disabled={isSaving}
              />
            ) : (
              <Text variant="body1">{field.state.value}</Text>
            )}
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
      {isDigestDeliveryEditable && (
        <form.AppField name="digestDelivery.repeatsWeeks">
          {(field) => (
            <field.Field>
              <div className="flex items-center gap-2">
                <FieldLabel htmlFor="ai-digest-never">
                  <Trans>AI Digest Subscription Email</Trans>
                </FieldLabel>
                <InfoPopover
                  title={t`AI Digest Subscription Email`}
                  body={
                    <Trans>
                      AI Digest emails go out on Mondays and will give you a summary of activity for
                      the entire period you select.
                    </Trans>
                  }
                />
              </div>
              {isEditing ? (
                <field.RadioGroup orientation="horizontal">
                  {digestDeliveryFrequencyOptions.map((option) => (
                    <Field key={option.value} orientation="horizontal">
                      <RadioGroupItem
                        value={option.value}
                        id={`ai-digest-${option.value}`}
                        disabled={(!isMe && !isAdmin) || isSaving}
                      />
                      <FieldLabel htmlFor={`ai-digest-${option.value}`}>{option.label}</FieldLabel>
                    </Field>
                  ))}
                </field.RadioGroup>
              ) : (
                <MultiOptionText
                  options={digestDeliveryFrequencyOptions}
                  value={field.state.value}
                  variant="body1"
                />
              )}
              <field.Error />
            </field.Field>
          )}
        </form.AppField>
      )}
    </div>
  );
};

export default UserDetailInfoFields;
